import { ApiError, NetworkError } from './errors';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  skipAuth?: boolean;
}

interface ApiClientConfig {
  baseUrl: string;
  onUnauthorized?: () => void;
}

class ApiClient {
  private baseUrl: string;
  private onUnauthorized?: () => void;
  private accessToken: string | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.onUnauthorized = config.onUnauthorized;
    // Try to restore token from sessionStorage
    this.accessToken = sessionStorage.getItem('whss-access-token');
  }

  /**
   * Set the access token for authenticated requests
   * Stores in both sessionStorage (for this origin) and a cookie (for cross-subdomain access)
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
    if (token) {
      sessionStorage.setItem('whss-access-token', token);
      // Also set a cross-domain cookie for terminal access from agent subdomains
      // Cookie is HttpOnly=false so terminal JS can read it, but Secure and SameSite=Lax
      const domain = window.location.hostname.includes('whss.dev') ? '.whss.dev' : window.location.hostname;
      document.cookie = `whss_access_token=${encodeURIComponent(token)}; domain=${domain}; path=/; max-age=3600; secure; samesite=lax`;
    } else {
      sessionStorage.removeItem('whss-access-token');
      // Clear the cookie
      const domain = window.location.hostname.includes('whss.dev') ? '.whss.dev' : window.location.hostname;
      document.cookie = `whss_access_token=; domain=${domain}; path=/; max-age=0; secure; samesite=lax`;
    }
  }

  /**
   * Get the current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Set the unauthorized callback (useful for lazy initialization with stores)
   */
  setOnUnauthorized(callback: () => void): void {
    this.onUnauthorized = callback;
  }

  private buildUrl(endpoint: string, params?: RequestOptions['params']): string {
    // Handle absolute URLs vs relative endpoints
    const baseUrl = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const fullUrl = `${baseUrl}${cleanEndpoint}`;
    
    const url = new URL(fullUrl, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, body, skipAuth, ...fetchOptions } = options;

    const url = this.buildUrl(endpoint, params);

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    // Add Authorization header if we have a token and auth isn't skipped
    if (!skipAuth && this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include', // Include cookies for session
        body: body ? JSON.stringify(body) : undefined,
      });

      // Handle no content responses
      if (response.status === 204) {
        return undefined as T;
      }

      // Parse response body
      const data = await response.json().catch(() => ({}));

      // Handle errors
      if (!response.ok) {
        const error = ApiError.fromResponse(response.status, data);

        // Handle unauthorized - trigger logout
        if (error.isUnauthorized && !skipAuth) {
          this.setAccessToken(null); // Clear invalid token
          this.onUnauthorized?.();
        }

        throw error;
      }

      return data as T;
    } catch (error) {
      // Re-throw API errors
      if (error instanceof ApiError) {
        throw error;
      }

      // Convert fetch errors to NetworkError
      if (error instanceof TypeError) {
        throw new NetworkError();
      }

      throw error;
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create singleton instance
// Note: onUnauthorized will be set lazily when auth store is available
export const api = new ApiClient({
  baseUrl: API_BASE_URL,
});

// Re-export errors
export { ApiError, NetworkError } from './errors';
